/**
 * The partner's notes stack: stamped admin notes (manual / legacy / ban)
 * and per-engagement reviews, searched / filtered / sorted server-side
 * (newest first by default; lowest or highest rating on a chosen
 * dimension), plus "Add note" — which posts immediately (independent of
 * the profile form's Save) and refetches. The free-form notes blob is
 * gone: every entry here is read-only once written and carries its
 * author and timestamp.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Pagination,
  Rating,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';

import { useNpNotes } from '@/hooks/useNetworkPartners';
import {
  NP_NOTE_DIMENSIONS,
  NP_NOTE_KIND_OPTIONS,
  NP_NOTE_SORTS,
  NP_REVIEW_DIMENSIONS,
  type NpNote,
  type NpNoteDimension,
  type NpNoteSort,
  type NpNotesQuery,
} from '@/types/network-partner';

/** Notes per page (the API caps a page at 200). */
export const NOTES_PAGE_SIZE = 50;

const SEARCH_DEBOUNCE_MS = 250;

const RATING_OPTIONS = ['1', '2', '3', '4', '5'].map((v) => ({ value: v, label: `${v} ★` }));

/** The toolbar's state; `kind` is `all` or one note kind, the rating
 * bounds are the Select values ('1'–'5') or null for "Any". */
export interface NotesFilters {
  kind: string;
  sort: NpNoteSort;
  dimension: NpNoteDimension;
  minRating: string | null;
  maxRating: string | null;
}

export const DEFAULT_NOTES_FILTERS: NotesFilters = {
  kind: 'all',
  sort: 'newest',
  dimension: 'overall',
  minRating: null,
  maxRating: null,
};

/** Whether a star rating is in play — the dimension picker only shows then. */
export function isRatingAware(filters: NotesFilters): boolean {
  return (
    filters.sort === 'rating_asc' ||
    filters.sort === 'rating_desc' ||
    filters.minRating !== null ||
    filters.maxRating !== null
  );
}

/** Set one rating bound, dragging the other along so min never exceeds
 * max (the API rejects an empty range). */
export function withRatingBound(
  filters: NotesFilters,
  which: 'minRating' | 'maxRating',
  value: string | null
): NotesFilters {
  const next = { ...filters, [which]: value };
  const crossed =
    next.minRating !== null &&
    next.maxRating !== null &&
    Number(next.minRating) > Number(next.maxRating);
  if (crossed) {
    return which === 'minRating' ? { ...next, maxRating: value } : { ...next, minRating: value };
  }
  return next;
}

/** Whether anything narrows the stack (sorting alone hides nothing). */
export function hasActiveFilters(filters: NotesFilters, search: string): boolean {
  return (
    search.trim() !== '' ||
    filters.kind !== 'all' ||
    filters.minRating !== null ||
    filters.maxRating !== null
  );
}

export function buildNotesQuery(filters: NotesFilters, search: string, page: number): NpNotesQuery {
  const ratingAware = isRatingAware(filters);
  return {
    page,
    pageSize: NOTES_PAGE_SIZE,
    q: search.trim() || undefined,
    kinds: filters.kind === 'all' ? undefined : [filters.kind],
    sort: filters.sort,
    dimension: ratingAware ? filters.dimension : undefined,
    minRating: filters.minRating === null ? undefined : Number(filters.minRating),
    maxRating: filters.maxRating === null ? undefined : Number(filters.maxRating),
  };
}

const KIND_META: Record<string, { label: string; color: string }> = {
  manual: { label: 'Note', color: 'gray' },
  legacy: { label: 'Legacy', color: 'gray' },
  ban: { label: 'Banned', color: 'red' },
  review: { label: 'Review', color: 'teal' },
};

export function noteAuthor(note: NpNote): string {
  if (note.kind === 'legacy') {
    return 'Migrated from CRM';
  }
  return note.author_email ?? 'Unknown';
}

export function NpNoteEntry({ note }: Readonly<{ note: NpNote }>) {
  const meta = KIND_META[note.kind] ?? { label: note.kind, color: 'gray' };
  return (
    <Card withBorder radius="sm" padding="sm" data-testid={`np-note-${note.id}`}>
      <Stack gap={6}>
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Group gap="xs" wrap="wrap">
            <Badge size="sm" variant="light" color={meta.color}>
              {meta.label}
            </Badge>
            <Text size="xs" c="dimmed">
              {noteAuthor(note)}
            </Text>
            {note.review && (
              <Anchor
                component={Link}
                to={`/reports/${note.review.report_id}`}
                size="xs"
                data-testid={`np-note-${note.id}-report`}
              >
                Report {note.review.report_id.slice(0, 8)}…
              </Anchor>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {note.created_at ? new Date(note.created_at).toLocaleString() : '—'}
          </Text>
        </Group>
        {note.review && (
          <Stack gap={2} data-testid={`np-note-${note.id}-rating`}>
            {NP_REVIEW_DIMENSIONS.map((dim) => (
              <Group key={dim.key} gap={6} wrap="nowrap">
                <Text size="xs" w={150}>
                  {dim.label}
                </Text>
                <Rating value={note.review?.[dim.key] ?? 0} readOnly size="xs" />
              </Group>
            ))}
            <Text size="xs" fw={500}>
              Overall {note.review.overall}
            </Text>
          </Stack>
        )}
        {note.body && (
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {note.body}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

interface NotesToolbarProps {
  search: string;
  onSearch: (value: string) => void;
  filters: NotesFilters;
  onChange: (next: NotesFilters) => void;
}

function NotesToolbar({ search, onSearch, filters, onChange }: Readonly<NotesToolbarProps>) {
  const update = (changes: Partial<NotesFilters>) => onChange({ ...filters, ...changes });
  return (
    <Group gap="xs" align="flex-end" wrap="wrap" data-testid="np-notes-toolbar">
      <TextInput
        size="xs"
        label="Search"
        placeholder="Text or author…"
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => onSearch(e.currentTarget.value)}
        w={200}
        data-testid="np-notes-search"
      />
      <Select
        size="xs"
        label="Kind"
        data={NP_NOTE_KIND_OPTIONS}
        value={filters.kind}
        onChange={(v) => update({ kind: v ?? 'all' })}
        allowDeselect={false}
        w={120}
        data-testid="np-notes-kind"
      />
      <Select
        size="xs"
        label="Sort"
        data={NP_NOTE_SORTS}
        value={filters.sort}
        onChange={(v) => update({ sort: (v as NpNoteSort | null) ?? 'newest' })}
        allowDeselect={false}
        w={150}
        data-testid="np-notes-sort"
      />
      {isRatingAware(filters) && (
        <Select
          size="xs"
          label="Rate by"
          data={NP_NOTE_DIMENSIONS}
          value={filters.dimension}
          onChange={(v) => update({ dimension: (v as NpNoteDimension | null) ?? 'overall' })}
          allowDeselect={false}
          w={170}
          data-testid="np-notes-dimension"
        />
      )}
      <Select
        size="xs"
        label="Min ★"
        placeholder="Any"
        data={RATING_OPTIONS}
        value={filters.minRating}
        onChange={(v) => onChange(withRatingBound(filters, 'minRating', v))}
        clearable
        w={90}
        data-testid="np-notes-min-rating"
      />
      <Select
        size="xs"
        label="Max ★"
        placeholder="Any"
        data={RATING_OPTIONS}
        value={filters.maxRating}
        onChange={(v) => onChange(withRatingBound(filters, 'maxRating', v))}
        clearable
        w={90}
        data-testid="np-notes-max-rating"
      />
    </Group>
  );
}

export function NpNotesPanel({ partnerId }: Readonly<{ partnerId: string }>) {
  const [filters, setFilters] = useState<NotesFilters>(DEFAULT_NOTES_FILTERS);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  // The page belongs to one filter combination: any change (including a
  // settled search) lands the reader back on page 1 without an effect.
  const filtersKey = JSON.stringify([filters, debouncedSearch.trim()]);
  const [paging, setPaging] = useState({ key: filtersKey, page: 1 });
  const page = paging.key === filtersKey ? paging.page : 1;
  const setPage = (next: number) => setPaging({ key: filtersKey, page: next });

  const { data, isLoading, addNote, isAdding } = useNpNotes(
    partnerId,
    buildNotesQuery(filters, debouncedSearch, page)
  );
  const [draft, setDraft] = useState('');
  const pages = data ? Math.ceil(data.total / NOTES_PAGE_SIZE) : 0;

  const submit = async () => {
    const body = draft.trim();
    if (!body) {
      return;
    }
    const note = await addNote(body);
    if (note) {
      setDraft('');
      setPage(1);
    }
  };

  return (
    <Stack gap="sm">
      <Textarea
        label="Add note"
        description="Stamped with the current time and your name; notes cannot be edited once added"
        autosize
        minRows={2}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        data-testid="np-note-draft"
      />
      <Group justify="flex-end">
        <Button
          size="xs"
          onClick={() => void submit()}
          loading={isAdding}
          disabled={!draft.trim()}
          data-testid="np-note-add"
        >
          Add note
        </Button>
      </Group>
      <NotesToolbar search={search} onSearch={setSearch} filters={filters} onChange={setFilters} />
      {isLoading && !data ? (
        <Group justify="center" py="sm">
          <Loader size="sm" />
        </Group>
      ) : !data || data.items.length === 0 ? (
        <Text size="sm" c="dimmed" data-testid="np-notes-empty">
          {hasActiveFilters(filters, debouncedSearch)
            ? 'No notes match these filters.'
            : 'No notes yet.'}
        </Text>
      ) : (
        <Stack gap="xs" data-testid="np-notes-list">
          {data.items.map((note) => (
            <NpNoteEntry key={note.id} note={note} />
          ))}
        </Stack>
      )}
      {pages > 1 ? (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={pages} size="sm" />
        </Group>
      ) : null}
    </Stack>
  );
}
